'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { AppInput } from '@/components/design/forms';
import {
  filterCountrySearchRows,
  getCountryLabel,
  isPickerCountryCode,
  normalizeCountryToIso,
} from '@/lib/countries/countryLabels';

export type CountrySearchInputProps = {
  value: string;
  onChange: (isoOrEmpty: string) => void;
  disabled?: boolean;
  error?: boolean;
  /** className на обёртке */
  className?: string;
  /** className на поле ввода (как у соседних AppInput) */
  inputClassName?: string;
  placeholder?: string;
  id?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
};

export function CountrySearchInput({
  value,
  onChange,
  disabled,
  error,
  className = '',
  inputClassName = '',
  placeholder,
  id,
  'aria-invalid': ariaInvalid,
}: CountrySearchInputProps) {
  const locale = useLocale();
  const uiLocale = typeof locale === 'string' && locale.length >= 2 ? locale.slice(0, 2) : 'en';
  const t = useTranslations('strings.countrySearch');

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const syncDraftFromValue = useCallback(() => {
    const v = value.trim();
    if (!v) {
      setDraft('');
      return;
    }
    if (isPickerCountryCode(v)) {
      setDraft(getCountryLabel(v, uiLocale));
      return;
    }
    setDraft(v);
  }, [value, uiLocale]);

  useEffect(() => {
    if (!open) syncDraftFromValue();
  }, [open, syncDraftFromValue]);

  const finalizeInput = useCallback(() => {
    const d = draftRef.current;
    const norm = normalizeCountryToIso(d);
    if (norm) {
      onChange(norm);
      return;
    }
    if (!d.trim()) onChange('');
  }, [onChange]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        if (openRef.current) finalizeInput();
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [finalizeInput]);

  const filtered = useMemo(
    () => filterCountrySearchRows(open ? draft : '', uiLocale),
    [open, draft, uiLocale],
  );

  const displayValue = open
    ? draft
    : value.trim() && isPickerCountryCode(value)
      ? getCountryLabel(value, uiLocale)
      : value;

  const handleFocus = () => {
    if (disabled) return;
    setOpen(true);
    syncDraftFromValue();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setDraft(v);
    setOpen(true);
  };

  const handleSelect = (code: string) => {
    onChange(code);
    setOpen(false);
    setDraft('');
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      if (
        openRef.current &&
        !wrapRef.current?.contains(document.activeElement)
      ) {
        finalizeInput();
        setOpen(false);
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      syncDraftFromValue();
    }
  };

  const showList = open && !disabled && filtered.length > 0;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <AppInput
        id={id}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoComplete="off"
        aria-invalid={ariaInvalid ?? (error ? true : undefined)}
        aria-expanded={open}
        aria-autocomplete="list"
        role="combobox"
        className={inputClassName}
        placeholder={placeholder}
      />
      {showList && (
        <ul
          className="absolute z-50 w-full mt-1 bg-white border border-fest-border-strong rounded-lg shadow-lg max-h-60 overflow-y-auto py-1"
          role="listbox"
        >
          {filtered.map((row) => (
            <li key={row.code} role="option">
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-fest-heading hover:bg-fest-canvas transition-colors"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(row.code)}
              >
                {getCountryLabel(row.code, uiLocale)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !disabled && filtered.length === 0 && draft.trim().length > 0 && (
        <p className="absolute z-50 w-full mt-1 rounded-lg border border-fest-border-strong bg-white px-3 py-2 text-xs text-fest-subtle-text shadow-lg">
          {t('empty')}
        </p>
      )}
    </div>
  );
}
